import { supabase } from '../lib/supabase';

/**
 * Employee Service - Handles all employee/driver CRUD operations
 * Manages chauffeurs and staff in the ADT Tracking system
 */

export const employeeService = {
  /**
   * Get all employees with optional filters
   * @param {Object} filters - Optional filters { zone, isActive }
   * @returns {Promise<Array>} List of employees
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('employees')?.select('*')?.order('created_at', { ascending: false });

      // Apply filters if provided
      if (filters?.zone) {
        query = query?.eq('zone', filters?.zone);
      }

      if (typeof filters?.isActive === 'boolean') {
        query = query?.eq('is_active', filters?.isActive);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        name: row?.name,
        phone: row?.phone,
        email: row?.email,
        role: row?.role,
        zone: row?.zone,
        isActive: row?.is_active,
        notes: row?.notes,
        authUserId: row?.auth_user_id,
        mustChangePassword: row?.must_change_password || false,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  },

  /**
   * Get employee by ID
   * @param {string} employeeId - Employee UUID
   * @returns {Promise<Object>} Employee details
   */
  async getById(employeeId) {
    try {
      const { data, error } = await supabase?.from('employees')?.select('*')?.eq('id', employeeId)?.single();

      if (error) throw error;

      // Convert to camelCase
      return {
        id: data?.id,
        name: data?.name,
        phone: data?.phone,
        email: data?.email,
        role: data?.role,
        zone: data?.zone,
        isActive: data?.is_active,
        notes: data?.notes,
        authUserId: data?.auth_user_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching employee:', error);
      throw error;
    }
  },

  /**
   * Create new employee
   * @param {Object} employeeData - Employee data in camelCase
   * @returns {Promise<Object>} Created employee
   */
  async create(employeeData) {
    try {
      const isDriverRole = ['chauffeur', 'DRIVER_FR']?.includes(employeeData?.role || 'chauffeur');
      const hasAuthLink = !!employeeData?.authUserId;

      // The DB constraint requires active drivers to have auth_user_id.
      // If creating a driver without an auth link, create them as inactive first.
      const isActive = isDriverRole && !hasAuthLink
        ? false
        : (employeeData?.isActive !== undefined ? employeeData?.isActive : true);

      const { data, error } = await supabase?.from('employees')?.insert({
          name: employeeData?.name,
          phone: employeeData?.phone || null,
          email: employeeData?.email || null,
          role: employeeData?.role || 'chauffeur',
          zone: employeeData?.zone || null,
          is_active: isActive,
          notes: employeeData?.notes || null,
          auth_user_id: employeeData?.authUserId || null,
          must_change_password: employeeData?.mustChangePassword !== undefined ? employeeData?.mustChangePassword : false,
        })?.select()?.single();

      if (error) throw error;

      // Convert to camelCase
      return {
        id: data?.id,
        name: data?.name,
        phone: data?.phone,
        email: data?.email,
        role: data?.role,
        zone: data?.zone,
        isActive: data?.is_active,
        notes: data?.notes,
        authUserId: data?.auth_user_id,
        mustChangePassword: data?.must_change_password || false,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  },

  /**
   * Update employee
   * FIXED: No longer silently reverts is_active — the admin decides the status.
   * @param {string} employeeId - Employee UUID
   * @param {Object} updates - Fields to update (camelCase)
   * @returns {Promise<Object>} Updated employee
   */
  async update(employeeId, updates) {
    try {
      console.log('📝 [employeeService.update] employeeId:', employeeId, 'updates:', updates);

      // Convert camelCase to snake_case
      const dbUpdates = {};
      if (updates?.name !== undefined) dbUpdates.name = updates?.name;
      if (updates?.phone !== undefined) dbUpdates.phone = updates?.phone;
      if (updates?.email !== undefined) dbUpdates.email = updates?.email;
      if (updates?.role !== undefined) dbUpdates.role = updates?.role;
      if (updates?.zone !== undefined) dbUpdates.zone = updates?.zone;
      if (updates?.isActive !== undefined) dbUpdates.is_active = updates?.isActive;
      if (updates?.notes !== undefined) dbUpdates.notes = updates?.notes;
      if (updates?.authUserId !== undefined) dbUpdates.auth_user_id = updates?.authUserId;
      if (updates?.mustChangePassword !== undefined) dbUpdates.must_change_password = updates?.mustChangePassword;

      console.log('📝 [employeeService.update] dbUpdates to Supabase:', dbUpdates);

      const { data, error } = await supabase
        ?.from('employees')
        ?.update(dbUpdates)
        ?.eq('id', employeeId)
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ [employeeService.update] Supabase error:', error);
        throw error;
      }

      console.log('✅ [employeeService.update] Updated successfully:', data);

      // Convert back to camelCase
      return {
        id: data?.id,
        name: data?.name,
        phone: data?.phone,
        email: data?.email,
        role: data?.role,
        zone: data?.zone,
        isActive: data?.is_active,
        notes: data?.notes,
        authUserId: data?.auth_user_id,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  },

  /**
   * Delete employee
   * @param {string} employeeId - Employee UUID
   * @returns {Promise<void>}
   */
  async delete(employeeId) {
    try {
      const { error } = await supabase?.from('employees')?.delete()?.eq('id', employeeId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting employee:', error);
      throw error;
    }
  },

  /**
   * Get active drivers (for dropdown selections)
   * @returns {Promise<Array>} List of active drivers
   */
  async getActiveDrivers() {
    try {
      const { data, error } = await supabase?.from('employees')?.select('id, name, zone')?.eq('is_active', true)?.order('name');

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        name: row?.name,
        zone: row?.zone
      }));
    } catch (error) {
      console.error('Error fetching active drivers:', error);
      throw error;
    }
  },

  /**
   * Link an existing auth user to an employee by email
   * Searches auth.users via user_profiles or admin_users for the auth_user_id
   */
  async linkAuthUserByEmail(employeeId, email) {
    try {
      console.log('🔗 [employeeService.linkAuthUser] Linking employee', employeeId, 'with email', email);

      // Try to find auth_user_id from user_profiles
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', email)
        ?.single();

      if (profile?.id) {
        const { data, error } = await supabase
          ?.from('employees')
          ?.update({ auth_user_id: profile?.id })
          ?.eq('id', employeeId)
          ?.select()
          ?.single();
        if (error) throw error;
        console.log('✅ [employeeService.linkAuthUser] Linked via user_profiles:', profile?.id);
        return { authUserId: profile?.id, source: 'user_profiles' };
      }

      // Try admin_users table
      const { data: adminUser } = await supabase
        ?.from('admin_users')
        ?.select('id')
        ?.eq('email', email)
        ?.single();

      if (adminUser?.id) {
        const { data, error } = await supabase
          ?.from('employees')
          ?.update({ auth_user_id: adminUser?.id })
          ?.eq('id', employeeId)
          ?.select()
          ?.single();
        if (error) throw error;
        console.log('✅ [employeeService.linkAuthUser] Linked via admin_users:', adminUser?.id);
        return { authUserId: adminUser?.id, source: 'admin_users' };
      }

      throw new Error(`Aucun compte Auth trouvé pour l'email: ${email}`);
    } catch (error) {
      console.error('Error linking auth user:', error);
      throw error;
    }
  }
};